import { Link } from "react-router-dom";
import { Globe, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const SUPPORT_EMAIL = "info.nomadroadmap@gmail.com";

const Section = ({ n, title, children }) => (
  <section className="space-y-3">
    <h2 className="text-xl font-semibold text-foreground">{n}. {title}</h2>
    {children}
  </section>
);

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-2 font-['Manrope']">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: November 5, 2026</p>

        <div className="space-y-8 text-muted-foreground leading-relaxed">
          <p>
            This policy explains what <span className="text-foreground font-medium">NomadRoadmap LLC</span> ("we", "us") collects when you use nomadroadmap.com, why, and the choices you have. We keep it short on purpose; if anything is unclear, email {SUPPORT_EMAIL}.
          </p>

          <Section n={1} title="What we collect">
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="text-foreground">Account details:</span> name, email address and a hashed password (we never store the password itself).</li>
              <li><span className="text-foreground">Planning profile:</span> citizenship, age range, current country, employment status, income and savings ranges, preferred regions, timeline and language comfort. You choose what to enter; income and savings are stored as ranges, not exact figures.</li>
              <li><span className="text-foreground">Your roadmap data:</span> countries you track, checklist progress, reminders, travel-day logs and any notes.</li>
              <li><span className="text-foreground">Documents you upload</span> to the vault (for example passport scans or proof of income). These often contain sensitive personal data; upload only what you need.</li>
              <li><span className="text-foreground">Billing:</span> Stripe collects and stores your card details; we never see your full card number. We store your Stripe customer and subscription IDs and your plan status.</li>
              <li><span className="text-foreground">Support messages</span> you send through the contact form or by email.</li>
              <li><span className="text-foreground">Technical data:</span> IP address and request logs kept by our hosting providers for security and troubleshooting. We do not currently run analytics or advertising trackers.</li>
            </ul>
          </Section>

          <Section n={2} title="How we use it">
            <p>Only to run the Service: to show you visa matches and checklists, store and return your documents, send reminders and account emails (password resets, receipts, replies to your support requests), process payments, prevent abuse, and comply with the law. We do not sell your personal data, and we do not use it for advertising.</p>
          </Section>

          <Section n={3} title="Who we share it with">
            <p>Only the providers needed to operate the Service, each under its own privacy terms: <span className="text-foreground">Stripe</span> (payments), <span className="text-foreground">Resend</span> (email delivery), <span className="text-foreground">MongoDB Atlas</span> (database and document storage, hosted in the United States), <span className="text-foreground">Render</span> and <span className="text-foreground">Vercel</span> (hosting). We may also disclose data if required by law or to protect our rights or users' safety.</p>
          </Section>

          <Section n={4} title="Where data is stored and international transfers">
            <p>Our systems are located in the United States. If you use the Service from outside the US (including the EU/EEA or UK), your data is transferred to and processed in the US. By using the Service you consent to this transfer; where required we rely on standard contractual clauses offered by our providers.</p>
          </Section>

          <Section n={5} title="Security">
            <p>All traffic is encrypted in transit (HTTPS). Passwords are hashed with bcrypt. Documents are stored in our database, which is encrypted at rest by the hosting provider, and are only accessible from your account. No system is perfectly secure, so please use a strong, unique password.</p>
          </Section>

          <Section n={6} title="How long we keep data">
            <p>For as long as your account exists. If you delete your account, your profile, roadmap data, reminders, support history and uploaded documents are deleted immediately. Billing records held by Stripe, and backups, may persist for a limited time afterwards as required by tax and accounting rules.</p>
          </Section>

          <Section n={7} title="Your choices and rights">
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="text-foreground">Access and update:</span> your profile is editable in the app.</li>
              <li><span className="text-foreground">Delete documents:</span> any time, from the Documents page.</li>
              <li><span className="text-foreground">Delete your account:</span> Profile → Delete account. This removes your data as described above and cancels your subscription.</li>
              <li><span className="text-foreground">Export:</span> email {SUPPORT_EMAIL} and we will send you a copy of your data.</li>
              <li><span className="text-foreground">Emails:</span> reminder emails can be turned off in the app; we still send essential account and billing emails.</li>
            </ul>
            <p>If you are in the EU/EEA, UK, California or another place with privacy laws, you have the rights those laws give you (access, correction, deletion, portability, objection, and the right to complain to your supervisory authority). Email {SUPPORT_EMAIL} to exercise any of them; we respond within 30 days.</p>
          </Section>

          <Section n={8} title="Cookies">
            <p>We use only the cookies and browser storage needed to keep you signed in. There are no advertising or tracking cookies, so there is nothing to opt out of. If we add analytics in the future we will update this policy first.</p>
          </Section>

          <Section n={9} title="Children">
            <p>The Service is for adults (18+). We do not knowingly collect data from anyone under 18; if you believe we have, email us and we will delete it.</p>
          </Section>

          <Section n={10} title="Changes">
            <p>If we make material changes we will email you or show a notice in the Service before they take effect.</p>
          </Section>

          <Section n={11} title="Contact">
            <p>NomadRoadmap LLC<br />{SUPPORT_EMAIL}</p>
          </Section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
